// frontend/src/pages/Vendors.jsx
// Module 8 alignment: add Privacy Act ADM checkbox (forward-dated),
//                     CPS230-AI-02/03 contract deadline note (1 July 2026).
// commit: fix(m8): align Vendors form with CPS230-AI-02/03 and Privacy Act ADM obligations

import { useState, useEffect, useCallback } from 'react';
import api from '../api';

const CPS230_STATUS_OPTIONS = [
  { value: 'not_assessed',         label: 'Not Assessed' },
  { value: 'under_review',         label: 'Under Review' },
  { value: 'compliant',            label: 'Compliant' },
  { value: 'remediation_required', label: 'Remediation Required' },
];

const EMPTY_FORM = {
  vendor_name:                      '',
  product_name:                     '',
  model_type:                       '',
  vendor_website:                   '',
  is_material_service_provider:     true,
  data_access_scope:                '',
  processes_personal_info:          false,
  subprocessors_disclosed:          false,
  subprocessors_list:               '',
  adm_applicable:                   false,   // NEW: Privacy Act ADM
  contract_expiry_date:             '',
  contract_cps230_compliant:        false,
  model_update_notification_clause: false,
  audit_inspection_rights:          false,
  incident_notification_clause:     false,
  data_handling_change_trigger:     false,
  termination_portability_clause:   false,
  last_reviewed_at:                 '',
  next_review_due:                  '',
  named_owner_id:                   '',
  cps230_status:                    'not_assessed',
  notes:                            '',
};

function CPS230StatusBadge({ status }) {
  const map = {
    compliant:            { label: 'Compliant',            cls: 'badge badge-green' },
    under_review:         { label: 'Under Review',         cls: 'badge badge-amber' },
    not_assessed:         { label: 'Not Assessed',         cls: 'badge badge-grey'  },
    remediation_required: { label: 'Remediation Required', cls: 'badge badge-red'   },
  };
  const { label, cls } = map[status] || map.not_assessed;
  return <span className={cls}>{label}</span>;
}

function ContractBadge({ expired, expiring_soon, expiry_date }) {
  if (expired)       return <span className="badge badge-red">Contract Expired</span>;
  if (expiring_soon) return <span className="badge badge-amber">Expiring &lt; 60 days</span>;
  if (expiry_date)   return <span className="badge badge-green">Contract Current</span>;
  return <span className="badge badge-grey">No Expiry Recorded</span>;
}

function ClauseFlag({ ok, label }) {
  return (
    <span className={`cps230-flag ${ok ? 'flag-ok' : 'flag-gap'}`} title={label}>
      {ok ? '✓' : '✗'} {label}
    </span>
  );
}

export default function Vendors() {
  const [vendors,   setVendors]   = useState([]);
  const [orgUsers,  setOrgUsers]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [view,      setView]      = useState('list');
  const [editing,   setEditing]   = useState(null);
  const [form,      setForm]      = useState(EMPTY_FORM);
  const [saving,    setSaving]    = useState(false);
  const [formError, setFormError] = useState(null);
  const [deleting,  setDeleting]  = useState(null);

  const fetchVendors = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.get('/vendors');
      setVendors(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load vendor register');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchOrgUsers = useCallback(async () => {
    try {
      const res = await api.get('/vendors/users/list');
      setOrgUsers(res.data);
    } catch (_) {}
  }, []);

  useEffect(() => { fetchVendors(); fetchOrgUsers(); }, [fetchVendors, fetchOrgUsers]);

  function openNew() {
    setEditing(null); setForm(EMPTY_FORM); setFormError(null); setView('form');
  }

  function openEdit(vendor) {
    setEditing(vendor.id);
    setForm({
      vendor_name:                      vendor.vendor_name || '',
      product_name:                     vendor.product_name || '',
      model_type:                       vendor.model_type || '',
      vendor_website:                   vendor.vendor_website || '',
      is_material_service_provider:     vendor.is_material_service_provider !== false,
      data_access_scope:                vendor.data_access_scope || '',
      processes_personal_info:          !!vendor.processes_personal_info,
      subprocessors_disclosed:          !!vendor.subprocessors_disclosed,
      subprocessors_list:               vendor.subprocessors_list || '',
      adm_applicable:                   !!vendor.adm_applicable,
      contract_expiry_date:             vendor.contract_expiry_date ? vendor.contract_expiry_date.slice(0,10) : '',
      contract_cps230_compliant:        !!vendor.contract_cps230_compliant,
      model_update_notification_clause: !!vendor.model_update_notification_clause,
      audit_inspection_rights:          !!vendor.audit_inspection_rights,
      incident_notification_clause:     !!vendor.incident_notification_clause,
      data_handling_change_trigger:     !!vendor.data_handling_change_trigger,
      termination_portability_clause:   !!vendor.termination_portability_clause,
      last_reviewed_at:                 vendor.last_reviewed_at ? vendor.last_reviewed_at.slice(0,10) : '',
      next_review_due:                  vendor.next_review_due  ? vendor.next_review_due.slice(0,10)  : '',
      named_owner_id:                   vendor.named_owner_id || '',
      cps230_status:                    vendor.cps230_status || 'not_assessed',
      notes:                            vendor.notes || '',
    });
    setFormError(null); setView('form');
  }

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  }

  async function handleSubmit(e) {
    e.preventDefault(); setSaving(true); setFormError(null);
    try {
      const payload = {
        ...form,
        contract_expiry_date: form.contract_expiry_date || null,
        last_reviewed_at:     form.last_reviewed_at     || null,
        next_review_due:      form.next_review_due       || null,
      };
      if (editing) {
        await api.put(`/vendors/${editing}`, payload);
      } else {
        await api.post('/vendors', payload);
      }
      await fetchVendors(); setView('list');
    } catch (err) {
      setFormError(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Remove this vendor from the register? This action is recorded in the audit trail.')) return;
    setDeleting(id);
    try {
      await api.delete(`/vendors/${id}`); await fetchVendors();
    } catch (err) {
      alert(err.response?.data?.error || 'Delete failed');
    } finally {
      setDeleting(null);
    }
  }

  const totalVendors   = vendors.length;
  const remediationDue = vendors.filter(v => v.cps230_status === 'remediation_required').length;
  const overdueReviews = vendors.filter(v => v.review_overdue).length;
  const contractIssues = vendors.filter(v => v.contract_expired || v.contract_expiring_soon).length;

  // ── List view ─────────────────────────────────────────────────────────────
  if (view === 'list') {
    return (
      <div className="page">
        <div className="page-header">
          <div>
            <h1>AI Vendor Register</h1>
            <p className="page-subtitle">
              CPS 230 §25 — Material AI Service Providers · Named accountability owner required per APRA AI letter
            </p>
          </div>
          <button className="btn btn-primary" onClick={openNew}>+ Add Vendor</button>
        </div>

        <div className="status-bar">
          <div className="status-card">
            <span className="status-count">{totalVendors}</span>
            <span className="status-label">Registered Vendors</span>
          </div>
          <div className={`status-card ${remediationDue > 0 ? 'status-card-alert' : ''}`}>
            <span className="status-count">{remediationDue}</span>
            <span className="status-label">Remediation Required</span>
          </div>
          <div className={`status-card ${overdueReviews > 0 ? 'status-card-alert' : ''}`}>
            <span className="status-count">{overdueReviews}</span>
            <span className="status-label">Reviews Overdue</span>
          </div>
          <div className={`status-card ${contractIssues > 0 ? 'status-card-alert' : ''}`}>
            <span className="status-count">{contractIssues}</span>
            <span className="status-label">Contract Issues</span>
          </div>
        </div>

        {loading && <p className="loading-text">Loading vendor register…</p>}
        {error   && <p className="error-text">{error}</p>}

        {!loading && !error && vendors.length === 0 && (
          <div className="empty-state">
            <p>No vendors registered yet.</p>
            <p className="empty-hint">
              APRA CPS 230 requires a register of all material AI service providers with a named
              accountability owner for each. Add your first vendor to begin producing compliance evidence.
            </p>
            <button className="btn btn-primary" onClick={openNew}>Add First Vendor</button>
          </div>
        )}

        {!loading && vendors.length > 0 && (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Vendor / Product</th>
                  <th>Material (§25)</th>
                  <th>CPS 230 Status</th>
                  <th>Named Owner</th>
                  <th>Contract (§29)</th>
                  <th>Review Due (§27)</th>
                  <th>Contract Clauses (§30)</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {vendors.map(v => (
                  <tr key={v.id} className={v.cps230_status === 'remediation_required' ? 'row-alert' : ''}>
                    <td>
                      <div className="vendor-name">{v.vendor_name}</div>
                      {v.product_name && <div className="vendor-sub">{v.product_name}</div>}
                      {v.model_type   && <div className="vendor-sub">{v.model_type}</div>}
                      {v.adm_applicable && (
                        <span style={{ fontSize: 10, color: '#b8860b', fontWeight: 600, background: '#fef3c7', padding: '1px 6px', borderRadius: 4, marginTop: 3, display: 'inline-block' }}>
                          ADM — Privacy Act
                        </span>
                      )}
                    </td>
                    <td>
                      <span className={v.is_material_service_provider ? 'badge badge-amber' : 'badge badge-grey'}>
                        {v.is_material_service_provider ? 'Material' : 'Non-Material'}
                      </span>
                    </td>
                    <td><CPS230StatusBadge status={v.cps230_status} /></td>
                    <td>
                      {v.named_owner_name || <span className="text-warning">Not assigned</span>}
                      {v.named_owner_email && <div className="vendor-sub">{v.named_owner_email}</div>}
                    </td>
                    <td>
                      <ContractBadge expired={v.contract_expired} expiring_soon={v.contract_expiring_soon} expiry_date={v.contract_expiry_date} />
                      {v.contract_expiry_date && (
                        <div className="date-sub">{new Date(v.contract_expiry_date).toLocaleDateString('en-AU')}</div>
                      )}
                    </td>
                    <td>
                      {v.next_review_due ? new Date(v.next_review_due).toLocaleDateString('en-AU') : '—'}
                      {v.review_overdue && <span className="badge badge-red" style={{marginLeft:'4px'}}>Overdue</span>}
                    </td>
                    <td>
                      <div className="cps230-flags">
                        <ClauseFlag ok={v.contract_cps230_compliant}      label="CPS 230" />
                        <ClauseFlag ok={v.audit_inspection_rights}        label="Audit Rights" />
                        <ClauseFlag ok={v.incident_notification_clause}   label="Incident Notify" />
                        <ClauseFlag ok={v.termination_portability_clause} label="Exit / Portability" />
                      </div>
                    </td>
                    <td>
                      <button className="btn btn-sm" onClick={() => openEdit(v)}>Edit</button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(v.id)} disabled={deleting === v.id}>
                        {deleting === v.id ? '…' : 'Remove'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  // ── Form view ─────────────────────────────────────────────────────────────
  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>{editing ? 'Edit Vendor Record' : 'Add AI Vendor'}</h1>
          <p className="page-subtitle">
            Fields map directly to CPS 230 material service provider obligations.
            Named accountability owner is mandatory per the APRA AI letter.
          </p>
        </div>
        <button className="btn btn-secondary" onClick={() => setView('list')}>← Back</button>
      </div>

      {formError && <div className="form-error">{formError}</div>}

      <form onSubmit={handleSubmit} className="vendor-form">

        {/* Section 1: Identity */}
        <fieldset className="form-section">
          <legend>Vendor Identity — CPS 230 §25</legend>
          <div className="form-row">
            <label>Vendor Name *
              <input name="vendor_name" value={form.vendor_name} onChange={handleChange} required placeholder="e.g. Microsoft, OpenAI, AWS" />
            </label>
            <label>Product / Service Name
              <input name="product_name" value={form.product_name} onChange={handleChange} placeholder="e.g. Azure OpenAI Service, Bedrock" />
            </label>
          </div>
          <div className="form-row">
            <label>AI Model Type
              <input name="model_type" value={form.model_type} onChange={handleChange} placeholder="e.g. Large Language Model, Computer Vision, Predictive Analytics" />
            </label>
            <label>Vendor Website
              <input name="vendor_website" value={form.vendor_website} onChange={handleChange} placeholder="https://" />
            </label>
          </div>
          <div className="form-row">
            <label>Named Accountability Owner (APRA AI Letter) *
              <select name="named_owner_id" value={form.named_owner_id} onChange={handleChange} required>
                <option value="">Select owner</option>
                {orgUsers.map(u => <option key={u.id} value={u.id}>{u.name} — {u.role.replace('_', ' ')}</option>)}
              </select>
            </label>
            <label>CPS 230 Compliance Status
              <select name="cps230_status" value={form.cps230_status} onChange={handleChange}>
                {CPS230_STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
          </div>
          <div className="form-row form-row-checks">
            <label className="checkbox-label">
              <input type="checkbox" name="is_material_service_provider" checked={form.is_material_service_provider} onChange={handleChange} />
              Material Service Provider (CPS 230 §25)
            </label>
          </div>
        </fieldset>

        {/* Section 2: Data, Privacy, and ADM */}
        <fieldset className="form-section">
          <legend>Data Access, Privacy, and Automated Decisions — CPS 230 §36 · Privacy Act</legend>
          <div className="form-row">
            <label>Data Access Scope (CPS 230 §36)
              <textarea name="data_access_scope" value={form.data_access_scope} onChange={handleChange} rows={3} placeholder="Describe what data this vendor can access, process, or train on" />
            </label>
          </div>
          <div className="form-row form-row-checks">
            <label className="checkbox-label">
              <input type="checkbox" name="processes_personal_info" checked={form.processes_personal_info} onChange={handleChange} />
              Processes Personal Information (Privacy Act APP 8)
            </label>
            <label className="checkbox-label">
              <input type="checkbox" name="subprocessors_disclosed" checked={form.subprocessors_disclosed} onChange={handleChange} />
              Subprocessors Named in Contract (CPS 230 §36)
            </label>
            <label className="checkbox-label" title="Privacy Act Tranche 1 — ADM disclosure obligations effective 10 December 2026">
              <input type="checkbox" name="adm_applicable" checked={form.adm_applicable} onChange={handleChange} />
              Used in automated decisions affecting individuals — Privacy Act ADM{' '}
              <span style={{ fontSize: 10, color: '#b8860b', fontWeight: 600, background: '#fef3c7', padding: '1px 6px', borderRadius: 4, marginLeft: 4 }}>
                Eff. 10 Dec 2026
              </span>
            </label>
          </div>
          {form.subprocessors_disclosed && (
            <div className="form-row">
              <label>Subprocessor List
                <textarea name="subprocessors_list" value={form.subprocessors_list} onChange={handleChange} rows={2} placeholder="List known subprocessors" />
              </label>
            </div>
          )}
          {form.adm_applicable && (
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '10px 14px', fontSize: 12, color: '#92400e', marginTop: 8 }}>
              From 10 December 2026, Privacy Act Tranche 1 requires disclosure of ADM use, individual opt-out rights, and ADM-specific data processing terms in vendor contracts. Record your compliance position in Notes.
            </div>
          )}
        </fieldset>

        {/* Section 3: Contract — with CPS230-AI-02/03 deadline note */}
        <fieldset className="form-section">
          <legend>Contract Terms — CPS 230 §29, §30, §33</legend>
          <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 6, padding: '10px 14px', fontSize: 12, color: '#0369a1', marginBottom: 14 }}>
            <strong>CPS 230 vendor contract deadline: 1 July 2026.</strong> All material AI service provider contracts must meet CPS 230 Attachment A requirements by this date (CPS230-AI-02/03).
          </div>
          <div className="form-row">
            <label>Contract Expiry Date (CPS 230 §29)
              <input type="date" name="contract_expiry_date" value={form.contract_expiry_date} onChange={handleChange} />
            </label>
          </div>
          <div className="form-row form-row-checks">
            <label className="checkbox-label">
              <input type="checkbox" name="contract_cps230_compliant" checked={form.contract_cps230_compliant} onChange={handleChange} />
              Contract Meets CPS 230 Requirements (§29)
            </label>
            <label className="checkbox-label">
              <input type="checkbox" name="model_update_notification_clause" checked={form.model_update_notification_clause} onChange={handleChange} />
              Model Update Notification Clause (§30)
            </label>
            <label className="checkbox-label">
              <input type="checkbox" name="audit_inspection_rights" checked={form.audit_inspection_rights} onChange={handleChange} />
              Audit and Inspection Rights (§30)
            </label>
            <label className="checkbox-label">
              <input type="checkbox" name="incident_notification_clause" checked={form.incident_notification_clause} onChange={handleChange} />
              Incident Notification Clause (§30)
            </label>
            <label className="checkbox-label">
              <input type="checkbox" name="data_handling_change_trigger" checked={form.data_handling_change_trigger} onChange={handleChange} />
              Data Handling Change Trigger Clause (§30)
            </label>
            <label className="checkbox-label">
              <input type="checkbox" name="termination_portability_clause" checked={form.termination_portability_clause} onChange={handleChange} />
              Termination and Data Portability Clause (§33)
            </label>
          </div>
        </fieldset>

        {/* Section 4: Review cadence */}
        <fieldset className="form-section">
          <legend>Review Cadence — CPS 230 §27</legend>
          <div className="form-row">
            <label>Last Reviewed Date
              <input type="date" name="last_reviewed_at" value={form.last_reviewed_at} onChange={handleChange} />
            </label>
            <label>Next Review Due
              <input type="date" name="next_review_due" value={form.next_review_due} onChange={handleChange} />
            </label>
          </div>
        </fieldset>

        {/* Section 5: Notes */}
        <fieldset className="form-section">
          <legend>Notes</legend>
          <label>Additional Notes
            <textarea name="notes" value={form.notes} onChange={handleChange} rows={3} placeholder="Any additional context relevant to this vendor relationship" />
          </label>
        </fieldset>

        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={() => setView('list')}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : editing ? 'Save Changes' : 'Add to Register'}
          </button>
        </div>
      </form>
    </div>
  );
}
